import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Download, Loader2, RefreshCw, Settings2, DollarSign } from "lucide-react";
import { toast } from "sonner";
import { PortalShell } from "@/components/PortalShell";
import { BreakdownTable } from "@/components/marketing/BreakdownTable";
import { FilterBar } from "@/components/marketing/FilterBar";
import { FunnelStages } from "@/components/marketing/FunnelStages";
import { JourneyDrawer } from "@/components/marketing/JourneyDrawer";
import { KpiCard } from "@/components/marketing/KpiCard";
import { MetricValue } from "@/components/marketing/MetricValue";
import {
  downloadCsv, fetchCampaign, fetchFilterOptions, fetchJourneys, fetchReport, money,
  num, pct, presetRange, priorPeriod, reasonText, roas,
} from "@/lib/marketing";

const Section = ({ title, subtitle, action, children, testid }) => (
  <section data-testid={testid}
    className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5 shadow-sm">
    <div className="flex flex-wrap items-start justify-between gap-2 mb-3">
      <div>
        <h2 className="text-base sm:text-lg font-black text-slate-900">{title}</h2>
        {subtitle && <p className="text-xs text-slate-500 mt-0.5">{subtitle}</p>}
      </div>
      {action}
    </div>
    {children}
  </section>
);

const Stat = ({ label, metric, format, testid }) => (
  <div className="flex justify-between gap-3 py-1.5 border-b border-slate-100 text-sm">
    <span className="text-slate-500">{label}</span>
    <span className="font-bold text-slate-900">
      <MetricValue metric={metric} format={format} testid={testid} />
    </span>
  </div>
);

const RECOMMENDATION_LABELS = {
  tank_clean: "Tank clean", filtration_service: "Filtration service",
  filtration_upgrade: "Filtration upgrade", uv_recommended: "UV",
  uv_service: "UV service", catchment_improvement: "Catchment / first flush",
  aware_lab_test: "AWARE lab test", plumbing_pump: "Plumbing / pump", other: "Other",
};

export default function CampaignPerformanceDetail() {
  const { campaignId } = useParams();
  const [campaign, setCampaign] = useState(null);
  const [options, setOptions] = useState(null);
  const [report, setReport] = useState(null);
  const [prior, setPrior] = useState(null);
  const [loading, setLoading] = useState(true);
  const [geoTab, setGeoTab] = useState("suburb");
  const [attrLens, setAttrLens] = useState("first_touch");
  const [journeys, setJourneys] = useState(null);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [openRef, setOpenRef] = useState(null);

  const [filters, setFilters] = useState({
    preset: "lifetime", date_mode: "acquisition", ...presetRange("lifetime"),
  });

  const query = useMemo(() => {
    const { preset, ...rest } = filters;
    return { ...rest, campaign: campaign?.reporting_campaign_id || undefined,
      campaign_key: campaign?.campaign_id };
  }, [filters, campaign]);

  useEffect(() => {
    fetchCampaign(campaignId).then(setCampaign).catch(() => toast.error("Campaign not found"));
    fetchFilterOptions().then(setOptions).catch(() => {});
  }, [campaignId]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const current = await fetchReport(query);
      setReport(current);
      const back = priorPeriod(query);
      setPrior(back ? await fetchReport({ ...query, ...back }) : null);
    } catch {
      toast.error("Unable to load campaign report");
    } finally {
      setLoading(false);
    }
  }, [query]);

  useEffect(() => { if (campaign) load(); }, [campaign, load]);
  useEffect(() => {
    if (!campaign) return;
    fetchJourneys({ ...query, page, page_size: 25, search })
      .then(setJourneys).catch(() => {});
  }, [campaign, query, page, search]);

  const k = report?.kpis || {};
  const v = report?.volumes || {};
  const pk = prior?.kpis || {};
  const pv = prior?.volumes || {};

  if (!report) {
    return (
      <PortalShell title="Campaign Performance">
        <p className="flex items-center gap-2 text-sm text-slate-500">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading campaign…
        </p>
      </PortalShell>
    );
  }

  const noActivity = v.health_check_bookings === 0
    && (v.aware_landing_visits?.value || 0) === 0;

  return (
    <PortalShell title="Campaign Performance" wide
      subtitle={campaign?.campaign_name}>
      {/* 1 · header + campaign status */}
      <section data-testid="campaign-header"
        className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5 shadow-sm mb-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-xl sm:text-2xl font-black text-slate-900">
              {campaign?.campaign_name}
            </h1>
            <p data-testid="campaign-status-line" className="text-xs text-slate-600 mt-1">
              <span className={`font-black ${campaign?.active ? "text-emerald-700"
                : "text-slate-500"}`}>
                {campaign?.active ? "ACTIVE" : "INACTIVE"}
              </span>
              {" · First-touch attribution · "}
              {campaign?.spend_source === "manual" ? "Manual spend" : "Meta API spend"}
              {" · "}{campaign?.reporting_currency}
              {report.data_quality.visit_tracking_since
                ? ` · Tracking since ${report.data_quality.visit_tracking_since}` : ""}
              {" · Updated "}{new Date(report.generated_at).toLocaleString("en-AU")}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Link to="/admin/marketing/campaign-settings" data-testid="header-link-settings"
              className="inline-flex items-center gap-1 rounded-full border border-slate-300
                px-3 py-1.5 text-xs font-bold hover:bg-slate-50">
              <Settings2 className="h-3 w-3" /> Campaign Settings
            </Link>
            <Link to="/admin/marketing/ad-spend" data-testid="header-link-ad-spend"
              className="inline-flex items-center gap-1 rounded-full border border-slate-300
                px-3 py-1.5 text-xs font-bold hover:bg-slate-50">
              <DollarSign className="h-3 w-3" /> Manage Ad Spend
            </Link>
            {["summary", "creative", "journeys"].map((kind) => (
              <button key={kind} data-testid={`export-${kind}`}
                onClick={() => downloadCsv(kind, query).then(
                  () => toast.success(`${kind} CSV exported`))}
                className="inline-flex items-center gap-1 rounded-full bg-slate-900 px-3
                  py-1.5 text-xs font-bold text-white hover:bg-slate-800">
                <Download className="h-3 w-3" /> {kind}
              </button>
            ))}
            <button data-testid="header-refresh" onClick={load}
              className="inline-flex items-center gap-1 rounded-full border border-slate-300
                px-3 py-1.5 text-xs font-bold hover:bg-slate-50">
              <RefreshCw className={`h-3 w-3 ${loading ? "animate-spin" : ""}`} /> Refresh
            </button>
          </div>
        </div>
      </section>

      {/* 2 · filters */}
      <div className="mb-4">
        <FilterBar filters={filters} options={options} onChange={setFilters}
          onReset={() => setFilters({ preset: "lifetime", date_mode: "acquisition",
            ...presetRange("lifetime") })} />
      </div>

      {noActivity && (
        <div data-testid="campaign-empty-state"
          className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-6 mb-4">
          <h2 className="text-lg font-black text-slate-900">No campaign activity yet</h2>
          <p className="text-sm text-slate-600 mt-1">
            AWARE will start building this report as visits, bookings and revenue arrive.
          </p>
          <div className="flex flex-wrap gap-2 mt-3">
            <a href="/tank-water-health-check" data-testid="empty-view-landing"
              className="rounded-full border border-slate-300 px-3 py-1.5 text-xs font-bold">
              View Landing Page
            </a>
            <Link to="/admin/marketing/campaign-settings" data-testid="empty-settings"
              className="rounded-full border border-slate-300 px-3 py-1.5 text-xs font-bold">
              Campaign Settings
            </Link>
            <Link to="/admin/marketing/ad-spend" data-testid="empty-add-spend"
              className="rounded-full bg-slate-900 px-3 py-1.5 text-xs font-bold text-white">
              Add Ad Spend
            </Link>
          </div>
        </div>
      )}

      <div className="space-y-4">
        {/* 3 · commercial headline KPIs */}
        <Section title="Commercial performance" testid="section-commercial"
          subtitle="Is this campaign producing commercial return?">
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-3">
            <KpiCard label="Ad Spend" testid="kpi-ad-spend" format={money}
              metric={{ value: report.spend.campaign_spend || null,
                        reason: "no_ad_spend", formula: "Manual campaign-level spend" }}
              prior={{ value: prior?.spend.campaign_spend ?? null }} />
            <KpiCard label="Health Check Bookings" testid="kpi-bookings" format={num}
              metric={{ value: v.health_check_bookings,
                        formula: "Campaign-attributed bookings" }}
              prior={{ value: pv.health_check_bookings ?? null }} />
            <KpiCard label="Combined Net Revenue" testid="kpi-combined-revenue"
              format={money} metric={k.combined_net_revenue}
              prior={pk.combined_net_revenue} />
            <KpiCard label="Combined ROAS" testid="kpi-combined-roas" format={roas}
              metric={k.combined_roas} prior={pk.combined_roas} />
            <KpiCard label="Net Revenue After Ad Spend" testid="kpi-net-after-spend"
              format={money} metric={k.net_revenue_after_ad_spend}
              prior={pk.net_revenue_after_ad_spend} />
          </div>
        </Section>

        {/* 4 · acquisition KPIs */}
        <Section title="Acquisition" testid="section-acquisition"
          subtitle="AWARE server-side measurement is the primary source.">
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-3">
            <KpiCard label="AWARE Landing Page Visits" testid="kpi-visits" format={num}
              metric={v.aware_landing_visits} prior={pv.aware_landing_visits}
              hint="Server-side, de-duplicated per session/day" />
            <KpiCard label="Health Check Starts" testid="kpi-starts" format={num}
              metric={v.health_check_starts} prior={pv.health_check_starts}
              hint={v.health_check_starts?.tracked_since
                ? `Tracked since ${v.health_check_starts.tracked_since}`
                : "Not tracked yet — never backfilled from bookings"} />
            <KpiCard label="Health Check Bookings" testid="kpi-bookings-acq" format={num}
              metric={{ value: v.health_check_bookings }}
              prior={{ value: pv.health_check_bookings ?? null }} />
            <KpiCard label="Cost per Landing Page Visit" testid="kpi-cost-per-visit"
              format={money} metric={k.cost_per_landing_page_visit}
              prior={pk.cost_per_landing_page_visit} />
            <KpiCard label="Cost per Health Check Booking" testid="kpi-cost-per-booking"
              format={money} metric={k.cost_per_health_check_booking}
              prior={pk.cost_per_health_check_booking} />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
            {[["GA4 Sessions", report.external_comparison.ga4_sessions],
              ["Meta Landing Page Views",
               report.external_comparison.meta_landing_page_views]].map(([label, m]) => (
              <div key={label} data-testid={`external-${label.split(" ")[0].toLowerCase()}`}
                className="rounded-xl border border-dashed border-slate-300 p-3 text-sm">
                <span className="text-slate-500">{label}: </span>
                <MetricValue metric={m} /> <span className="text-[11px] text-slate-500">
                  ({reasonText(m.reason)})
                </span>
              </div>
            ))}
          </div>
        </Section>

        {/* 5 · main funnel */}
        <Section title="Health Check funnel" testid="section-funnel">
          <FunnelStages stages={[
            { key: "visits", label: "Landing Page Visits", metric: v.aware_landing_visits },
            { key: "starts", label: "Health Check Starts", metric: v.health_check_starts,
              conversion: { value: null, reason: "not_measurable_with_these_filters" },
              note: v.health_check_starts?.tracked_since
                ? null : "Not tracked before start tracking began" },
            { key: "booked", label: "Health Check Booked",
              metric: { value: v.health_check_bookings },
              conversion: { value: null, reason: "not_measurable_with_these_filters" } },
            { key: "attended", label: "Attended", metric: { value: v.attended },
              conversion: k.attendance_rate, conversionLabel: "Attendance rate" },
            { key: "completed", label: "Completed", metric: { value: v.completed },
              conversion: k.completion_rate, conversionLabel: "Completion rate" },
          ]} />
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4">
            <Stat label="Attendance rate" metric={k.attendance_rate} format={pct}
              testid="rate-attendance" />
            <Stat label="Completion rate" metric={k.completion_rate} format={pct}
              testid="rate-completion" />
            <Stat label="No-show rate" metric={k.no_show_rate} format={pct}
              testid="rate-no-show" />
            <Stat label="Cancellation rate" metric={k.cancellation_rate} format={pct}
              testid="rate-cancellation" />
          </div>
        </Section>

        {/* 6 · post-completion split */}
        <Section title="After the Health Check" testid="section-split"
          subtitle="Completed Health Checks branch into AWARE laboratory testing and
            Pristine Water Systems service work.">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div data-testid="branch-aware-lab"
              className="rounded-xl border border-sky-200 bg-sky-50/50 p-4">
              <p className="text-xs font-black uppercase tracking-wider text-sky-800">
                AWARE lab · Purchased → Collected → Net revenue
              </p>
              <Stat label="Paid lab orders" metric={{ value: v.paid_lab_tests }}
                format={num} testid="lab-paid-orders" />
              <Stat label="Lab conversion (÷ completed)" metric={k.lab_conversion_rate}
                format={pct} testid="lab-conversion" />
              <Stat label="Lab conversion (÷ booked)"
                metric={k.lab_conversion_rate_per_booking} format={pct}
                testid="lab-conversion-booked" />
              <Stat label="Samples collected" metric={{ value: v.samples_collected }}
                format={num} testid="lab-samples" />
              <Stat label="Net lab revenue" metric={k.aware_net_lab_revenue}
                format={money} testid="lab-net-revenue" />
              <Stat label="Average order value"
                metric={{ value: v.paid_lab_tests
                  ? Math.round((v.aware_net_lab_revenue / v.paid_lab_tests) * 100) / 100
                  : null, reason: "no_completed_health_checks" }}
                format={money} testid="lab-aov" />
            </div>
            <div data-testid="branch-pws"
              className="rounded-xl border border-emerald-200 bg-emerald-50/50 p-4">
              <p className="text-xs font-black uppercase tracking-wider text-emerald-800">
                PWS service · Recommendation → Quote → Job → Net revenue
              </p>
              <Stat label="Follow-ups created" metric={{ value: v.follow_ups_created }}
                format={num} testid="pws-follow-ups" />
              <Stat label="Recommendation rate (÷ completed)" metric={k.recommendation_rate}
                format={pct} testid="pws-recommendation-rate" />
              <Stat label="Recommendation per booking" metric={k.recommendation_per_booking}
                format={pct} testid="pws-recommendation-booked" />
              <Stat label="Quotes created" metric={{ value: v.quotes_created }}
                format={num} testid="pws-quotes" />
              <Stat label="Quote conversion" metric={k.quote_conversion_rate}
                format={pct} testid="pws-quote-conversion" />
              <Stat label="Jobs booked" metric={{ value: v.pws_jobs_booked }}
                format={num} testid="pws-jobs" />
              <Stat label="Job conversion" metric={k.job_conversion_rate} format={pct}
                testid="pws-job-conversion" />
              <Stat label="Quoted value" metric={{ value: v.pws_quoted_value }}
                format={money} testid="pws-quoted-value" />
              <Stat label="Booked value" metric={{ value: v.pws_booked_value }}
                format={money} testid="pws-booked-value" />
              <Stat label="Net PWS revenue" metric={k.pws_net_attributed_revenue}
                format={money} testid="pws-net-revenue" />
            </div>
          </div>
        </Section>

        {/* 7 · revenue panels */}
        <Section title="Revenue" testid="section-revenue"
          subtitle="ROAS always uses net revenue received — never quoted or booked value.">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div data-testid="revenue-panel-lab" className="rounded-xl border p-4">
              <p className="text-xs font-black uppercase tracking-wider text-slate-600">
                AWARE lab revenue
              </p>
              <Stat label="Gross lab sales" metric={{ value: v.aware_net_lab_revenue
                + (report.spend.campaign_spend ? 0 : 0) }} format={money}
                testid="lab-gross-panel" />
              <Stat label="Net lab revenue" metric={k.aware_net_lab_revenue}
                format={money} />
              <Stat label="Paid lab tests" metric={{ value: v.paid_lab_tests }} format={num} />
              <Stat label="AWARE ROAS" metric={k.aware_roas} format={roas}
                testid="roas-aware" />
            </div>
            <div data-testid="revenue-panel-pws" className="rounded-xl border p-4">
              <p className="text-xs font-black uppercase tracking-wider text-slate-600">
                AWARE-recorded PWS revenue
              </p>
              <Stat label="Quoted value" metric={{ value: v.pws_quoted_value }} format={money} />
              <Stat label="Booked value" metric={{ value: v.pws_booked_value }} format={money} />
              <Stat label="Net attributed PWS revenue"
                metric={k.pws_net_attributed_revenue} format={money} />
              <Stat label="PWS ROAS" metric={k.pws_roas} format={roas} testid="roas-pws" />
            </div>
            <div data-testid="revenue-panel-combined" className="rounded-xl border p-4">
              <p className="text-xs font-black uppercase tracking-wider text-slate-600">
                Combined
              </p>
              <Stat label="Combined net revenue" metric={k.combined_net_revenue}
                format={money} />
              <Stat label="Ad spend"
                metric={{ value: report.spend.campaign_spend || null,
                          reason: "no_ad_spend" }} format={money} />
              <Stat label="Net revenue after ad spend"
                metric={k.net_revenue_after_ad_spend} format={money} />
              <Stat label="Combined ROAS" metric={k.combined_roas} format={roas}
                testid="roas-combined" />
              <Stat label="Revenue per completed Health Check"
                metric={k.revenue_per_completed_health_check} format={money} />
              <Stat label="Revenue per booking" metric={k.revenue_per_booking}
                format={money} />
            </div>
          </div>
        </Section>

        {/* 8 · creative */}
        <Section title="Creative Performance" testid="section-creative"
          subtitle="Creative ROAS uses real creative-level spend only — campaign spend is
            never apportioned.">
          <BreakdownTable testid="table-creative" rows={report.breakdowns.by_creative}
            sortable={["bookings", "combined_net_revenue", "roas"]}
            columns={[
              { key: "utm_content", label: "Creative" },
              { key: "bookings", label: "Bookings", format: num },
              { key: "attended", label: "Attended", format: num },
              { key: "completed", label: "Completed", format: num },
              { key: "paid_lab_tests", label: "Lab purchases", format: num },
              { key: "aware_net_lab_revenue", label: "Lab revenue", format: money },
              { key: "follow_ups", label: "Follow-ups", format: num },
              { key: "quotes_created", label: "Quotes", format: num },
              { key: "jobs_booked", label: "Jobs", format: num },
              { key: "pws_net_attributed_revenue", label: "PWS revenue", format: money },
              { key: "combined_net_revenue", label: "Combined revenue", format: money },
              { key: "ad_spend", label: "Spend", format: money },
              { key: "roas", label: "ROAS", format: roas, metric: true },
            ]} />
        </Section>

        {/* 9 · recommendations */}
        <Section title="What Health Checks Are Generating" testid="section-recommendations"
          subtitle="Click a row to filter the whole page by that recommendation.">
          <BreakdownTable testid="table-recommendations"
            rows={report.breakdowns.by_recommendation_type.map((r) => ({
              ...r, label: RECOMMENDATION_LABELS[r.recommendation_type]
                || r.recommendation_type }))}
            onRowClick={(row) => setFilters((f) => ({
              ...f, recommendation_type: row.recommendation_type }))}
            footnote={report.breakdowns.recommendation_note}
            columns={[
              { key: "label", label: "Recommendation" },
              { key: "bookings", label: "Recommended", format: num },
              { key: "follow_ups", label: "Follow-ups", format: num },
              { key: "quotes_created", label: "Quotes", format: num },
              { key: "jobs_booked", label: "Booked jobs", format: num },
              { key: "pws_net_attributed_revenue", label: "Net revenue", format: money },
            ]} />
        </Section>

        {/* 10 · geography */}
        <Section title="Performance by Area" testid="section-geography"
          subtitle={report.breakdowns.geography_note}
          action={
            <div className="flex gap-1">
              {[["suburb", "Suburb"], ["postcode", "Postcode"], ["area", "Region"]].map(
                ([key, label]) => (
                  <button key={key} data-testid={`geo-tab-${key}`} onClick={() => setGeoTab(key)}
                    className={`rounded-full px-3 py-1 text-xs font-bold border ${
                      geoTab === key ? "bg-slate-900 text-white border-slate-900"
                        : "border-slate-300 text-slate-600"}`}>
                    {label}
                  </button>
                ))}
            </div>
          }>
          <BreakdownTable testid={`table-geo-${geoTab}`}
            rows={geoTab === "suburb" ? report.breakdowns.by_suburb
              : geoTab === "postcode" ? report.breakdowns.by_postcode
                : report.breakdowns.by_area}
            columns={[
              { key: geoTab, label: geoTab === "area" ? "Region" : geoTab },
              { key: "bookings", label: "Bookings", format: num },
              { key: "attended", label: "Attended", format: num },
              { key: "completed", label: "Completed", format: num },
              { key: "paid_lab_tests", label: "Lab purchases", format: num },
              { key: "aware_net_lab_revenue", label: "Lab revenue", format: money },
              { key: "follow_ups", label: "Follow-ups", format: num },
              { key: "jobs_booked", label: "PWS jobs", format: num },
              { key: "pws_net_attributed_revenue", label: "PWS revenue", format: money },
              { key: "combined_net_revenue", label: "Combined revenue", format: money },
              { key: "roas", label: "ROAS", format: roas, metric: true },
            ]} />
        </Section>

        {/* 11 · technician / process */}
        <Section title="Health Check Delivery" testid="section-technician"
          subtitle="Process quality and coaching — not a ranking.">
          <BreakdownTable testid="table-technician" rows={report.breakdowns.by_technician}
            columns={[
              { key: "technician", label: "Technician" },
              { key: "bookings", label: "Assigned", format: num },
              { key: "attended", label: "Attended", format: num },
              { key: "completed", label: "Completed", format: num },
              { key: "no_show", label: "No shows", format: num },
              { key: "paid_lab_tests", label: "Lab purchases", format: num },
              { key: "follow_ups", label: "Follow-ups generated", format: num },
              { key: "jobs_booked", label: "Jobs booked", format: num },
              { key: "combined_net_revenue", label: "Attributed revenue", format: money },
            ]} />
        </Section>

        {/* 12 · attribution */}
        <Section title="Attribution" testid="section-attribution"
          subtitle={`Primary revenue attribution: First touch. ${
            report.attribution_analysis.note}`}
          action={
            <div className="flex gap-1">
              {[["first_touch", "First Touch"], ["last_touch", "Last Touch"],
                ["assists", "Assists"]].map(([key, label]) => (
                <button key={key} data-testid={`attr-lens-${key}`} onClick={() => setAttrLens(key)}
                  className={`rounded-full px-3 py-1 text-xs font-bold border ${
                    attrLens === key ? "bg-slate-900 text-white border-slate-900"
                      : "border-slate-300 text-slate-600"}`}>
                  {label}
                </button>
              ))}
            </div>
          }>
          <p className="text-[11px] text-slate-500 mb-2">
            This toggle changes only this section — the revenue and ROAS cards above always
            use first touch.
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
            <Stat label="First-touch bookings"
              metric={{ value: report.attribution_analysis.first_touch_bookings }}
              format={num} testid="attr-first-touch" />
            <Stat label="Last-touch bookings"
              metric={{ value: report.attribution_analysis.last_touch_bookings }}
              format={num} testid="attr-last-touch" />
            <Stat label="Assisted conversions"
              metric={{ value: report.attribution_analysis.assisted_conversions }}
              format={num} testid="attr-assisted" />
            <Stat label="Direct-return conversions"
              metric={{ value: report.attribution_analysis.direct_return_conversions }}
              format={num} testid="attr-direct" />
          </div>
          <BreakdownTable testid="table-attribution"
            rows={report.attribution_analysis.by_channel}
            columns={[
              { key: "channel", label: "Channel" },
              ...(attrLens === "first_touch"
                ? [{ key: "first_touch_bookings", label: "First-touch bookings", format: num }]
                : attrLens === "last_touch"
                  ? [{ key: "last_touch_bookings", label: "Last-touch bookings", format: num }]
                  : [{ key: "assists", label: "Assists", format: num }]),
            ]} />
        </Section>

        {/* 13 · spend reconciliation */}
        <Section title="Spend Reconciliation" testid="section-spend-reconciliation"
          action={
            <Link to="/admin/marketing/ad-spend" data-testid="reconciliation-manage-spend"
              className="rounded-full bg-slate-900 px-3 py-1.5 text-xs font-bold text-white">
              MANAGE AD SPEND
            </Link>
          }>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Stat label="Spend source" metric={{ value: report.spend.source }} />
            <Stat label="Campaign spend"
              metric={{ value: report.spend.campaign_spend || null, reason: "no_ad_spend" }}
              format={money} testid="recon-campaign-spend" />
            <Stat label="Creative spend allocated"
              metric={{ value: report.spend.creative_total || null,
                        reason: "creative_spend_unavailable" }}
              format={money} testid="recon-creative-spend" />
            <Stat label="Difference"
              metric={{ value: Object.values(report.spend_reconciliation)[0]?.difference
                ?? null, reason: "no_ad_spend" }} format={money} testid="recon-difference" />
          </div>
          {Object.entries(report.spend_reconciliation).map(([cid, rec]) => (
            <p key={cid} data-testid={`recon-verdict-${cid}`}
              className="text-xs text-slate-600 mt-2">
              <span className="font-black uppercase">{rec.verdict.replace(/_/g, " ")}</span>
              {" — "}{rec.note}
            </p>
          ))}
          {!Object.keys(report.spend_reconciliation).length && (
            <p data-testid="recon-no-spend" className="text-xs text-slate-600 mt-2">
              No spend entered for this period. ROAS unavailable — no ad spend has been
              entered for this period.
            </p>
          )}
        </Section>

        {/* 14 · data quality */}
        <Section title="Data Quality" testid="section-data-quality"
          subtitle={report.data_quality.note}
          action={
            <span data-testid="data-quality-overall"
              className={`rounded-full px-2.5 py-1 text-[11px] font-black border ${
                report.data_quality.overall === "healthy"
                  ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                  : report.data_quality.overall === "attention"
                    ? "bg-amber-50 text-amber-700 border-amber-200"
                    : "bg-slate-100 text-slate-600 border-slate-200"}`}>
              {report.data_quality.overall.toUpperCase()}
            </span>
          }>
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {report.data_quality.checks.map((c) => (
              <li key={c.key} data-testid={`data-quality-${c.key}`}
                className="rounded-xl border border-slate-200 p-3 text-sm">
                <span className={`inline-block h-2 w-2 rounded-full mr-2 ${
                  c.state === "healthy" ? "bg-emerald-500"
                    : c.state === "attention" ? "bg-amber-500" : "bg-slate-400"}`} />
                <span className="font-bold text-slate-800">{c.label}</span>
                {c.detail && <p className="text-[11px] text-slate-500 mt-1">{c.detail}</p>}
              </li>
            ))}
          </ul>
        </Section>

        {/* 15 · journeys */}
        <Section title="Campaign Journeys" testid="section-journeys"
          subtitle="Click any row for the full first-click-to-revenue story."
          action={
            <div className="flex gap-2">
              <input data-testid="journeys-search" value={search}
                onChange={(e) => { setPage(1); setSearch(e.target.value); }}
                placeholder="Search reference, customer, suburb…"
                className="rounded-lg border border-slate-300 px-3 py-1.5 text-xs w-56" />
              <button data-testid="journeys-export"
                onClick={() => downloadCsv("journeys", query)}
                className="rounded-full border border-slate-300 px-3 py-1.5 text-xs font-bold">
                CSV
              </button>
            </div>
          }>
          <BreakdownTable testid="table-journeys" rows={journeys?.journeys || []}
            onRowClick={(row) => setOpenRef(row.reference)}
            sortable={["booking_date", "combined_revenue"]}
            empty="No campaign journeys for the current filters."
            columns={[
              { key: "reference", label: "Reference" },
              { key: "customer", label: "Customer" },
              { key: "suburb", label: "Suburb" },
              { key: "first_touch_source", label: "First touch" },
              { key: "last_touch_source", label: "Last touch" },
              { key: "creative", label: "Creative" },
              { key: "booking_date", label: "Booked" },
              { key: "appointment", label: "Appointment" },
              { key: "status", label: "Status" },
              { key: "technician", label: "Technician" },
              { key: "lab_package", label: "Lab package" },
              { key: "lab_net", label: "Lab net", format: money },
              { key: "recommendations", label: "Recommendations" },
              { key: "follow_up_status", label: "Follow-up" },
              { key: "pws_job", label: "PWS job" },
              { key: "pws_revenue", label: "PWS revenue", format: money },
              { key: "combined_revenue", label: "Combined", format: money },
            ]} />
          {journeys && journeys.pages > 1 && (
            <div className="flex items-center justify-between mt-3 text-xs">
              <button data-testid="journeys-prev" disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
                className="rounded-full border px-3 py-1.5 font-bold disabled:opacity-40">
                Previous
              </button>
              <span data-testid="journeys-page">
                Page {journeys.page} of {journeys.pages} · {journeys.total} journeys
              </span>
              <button data-testid="journeys-next" disabled={page >= journeys.pages}
                onClick={() => setPage((p) => p + 1)}
                className="rounded-full border px-3 py-1.5 font-bold disabled:opacity-40">
                Next
              </button>
            </div>
          )}
        </Section>
      </div>

      <JourneyDrawer reference={openRef} onClose={() => setOpenRef(null)} />
    </PortalShell>
  );
}
